import { FC, useEffect, useRef, RefObject } from "react"
import ReactDOM from "react-dom"
import { motion } from "framer-motion"

import useCloseOnEsc from "Hooks/UI/useCloseOnEsc"
import IconFactory from "UI/IconFactory"

import { ModalPropTypes } from "./Modal.types"
import * as S from "./Modal.styles"

const rootNode =
  document.getElementById("modal") || document.createElement("div")
rootNode.setAttribute("id", "modal")
document.body.appendChild(rootNode)

// Helper function to get all focusable elements within a container
const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = [
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "a[href]",
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(", ")

  return Array.from(
    container.querySelectorAll(focusableSelectors)
  ) as HTMLElement[]
}

const Modal: FC<ModalPropTypes> = ({
  title,
  body,
  onClick,
  fullWidth,
  maxWidth,
}) => {
  const ref = useRef() as RefObject<HTMLDivElement>
  const triggeringElementRef = useRef<HTMLElement | null>(null)

  useCloseOnEsc(onClick)

  // Store reference to the element that triggered the modal
  useEffect(() => {
    triggeringElementRef.current = document.activeElement as HTMLElement
  }, [])

  // Focus the close button or first focusable element when modal opens
  const focusInitialElement = () => {
    if (ref.current) {
      const focusableElements = getFocusableElements(ref.current)
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      }
    }
  }

  // Focus management and trap setup
  useEffect(() => {
    document.body.style.overflowY = "hidden"

    // Focus trap handler
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !ref.current) return

      const focusableElements = getFocusableElements(ref.current)
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey) {
        // Shift + Tab: if focused on first element, move to last
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else if (document.activeElement === lastElement) {
        // Tab: if focused on last element, move to first
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.body.style.overflowY = "visible"

      // Return focus to triggering element when modal closes
      if (triggeringElementRef.current && triggeringElementRef.current.focus) {
        triggeringElementRef.current.focus()
      }
    }
  }, [])

  return ReactDOM.createPortal(
    <S.StyledModal
      as={motion.div}
      data-testid="modal"
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ ease: "easeOut", duration: 0.3 }}
    >
      <S.ModalBackdrop onClick={onClick} />
      <S.ModalInner
        as={motion.div}
        ref={ref}
        onAnimationComplete={focusInitialElement}
        animate={{ y: 30 }}
        exit={{ y: 0, opacity: 0 }}
        transition={{ ease: "easeOut", duration: 0.3 }}
        $fullWidth={fullWidth}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        $maxWidth={maxWidth}
      >
        {title && (
          <>
            <div className="head">
              {title && <h3 id="modal-title">{title}</h3>}
              <button aria-label="Close Modal" onClick={onClick} type="button">
                <IconFactory name="close" />
              </button>
            </div>
            <hr />
          </>
        )}

        <div className="body">
          <div className="innerBody">{body}</div>
        </div>
      </S.ModalInner>
    </S.StyledModal>,
    rootNode
  )
}

export default Modal
